import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EFFORT_SCALES, type EffortScale } from "@heraion/shared";
import { SettingsPillChoice, resolveTheme } from "@heraion/ui";

import { useShellProfile } from "../ThemeProfileBridge";
import { useThemePalette } from "../theme";

// web-rnw's LoggerHostDeps.EffortScalePicker (RW-18) — the effort-scale
// (RIR|RPE) preference card the Logger's Settings sheet mounts. Self-
// contained, zero props, owns its own profile round-trip, same contract
// mobile's apps/mobile/src/screens/EffortScalePicker.tsx implements. Web
// twin of apps/web-rnw/src/EffortScreen.tsx (the full Settings → Effort
// page) minus the page chrome/loading screen — this mounts inline inside
// the Logger's sheet, so there is no full-screen loading state to show.
export function EffortScalePicker() {
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const { t } = useTranslation();
  const { doc, saveField } = useShellProfile();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const current = (doc?.["effortScale"] as EffortScale | undefined) ?? "rir";

  const onPick = async (next: EffortScale) => {
    if (saving || current === next) return;
    setSaving(true);
    setError(null);
    try {
      await saveField("effortScale", next);
    } catch {
      setError("api_error_save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsPillChoice<EffortScale>
      theme={theme}
      label={t("mobile.effortSettings.scaleLabel", { defaultValue: "Effort scale" })}
      help={t("mobile.effortSettings.scaleHelp", {
        defaultValue:
          "Which scale the Logger uses. RIR = reps left in the tank; RPE = perceived exertion. RPE = 10 − RIR, so they are the same effort on two scales.",
      })}
      options={EFFORT_SCALES.map((scale) => ({ value: scale, label: scale.toUpperCase() }))}
      value={current}
      onPick={(s) => void onPick(s)}
      saving={saving}
      error={error}
    />
  );
}
